import type {ReactElement} from 'react';
import {SocialPlatform} from '@Components/social-media/social-media.types';
import {SocialPostCaption} from '@Components/social-media-wizard/components/social-post-caption';
import {FieldSpacer} from '@Components/social-media-wizard/components/field-spacer';
import {LinkedInPrivacySelector} from '@Components/social-media-wizard/components/linkedin-privacy-selector';
import {TiktokPublishingOptions} from '@Components/social-media-wizard/components/tiktok-publishing-options';
import {TitleInput} from '@Components/social-media-wizard/components/title-input';
import {YouTubePrivacySelector} from '@Components/social-media-wizard/components/youtube-privacy-selector';
import {PinterestBoardId} from '@Components/social-media-wizard/components/pinterest-board-id';
import {PinterestWebsiteLink} from '@Components/social-media-wizard/components/pinterest-website-link';
import {ReelToggler} from '@Components/social-media-wizard/components/reel-toggler';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {useAccountIdsToPublishTo} from '@Components/social-media/social-media.hooks';
import type {PlatformPublishFieldsProps} from './platform-publish-fields.types';
import {ReelMusicField} from './components/reel-music-field/reel-music-field';

export function PlatformPublishFields({
  account,
  isVideoDesign,
  titleErrorMessage,
  setTitleError,
  isAnyPosterResizingForForAnyMediaType,
  isReelMediaType,
  onReelToggle,
}: PlatformPublishFieldsProps): ReactElement | null {
  const accountIdsToPublishTo = useAccountIdsToPublishTo();

  const getSpacerAfterCaption = (): ReactElement | null => {
    if (accountIdsToPublishTo.length > 1) {
      return null;
    }

    return <FieldSpacer />;
  };

  const renderTitleError = (): ReactElement | null => {
    if (!titleErrorMessage) {
      return null;
    }
    return (
      <div className="flex-row-align-center spacing-m-t-2">
        <Icon icon="icon-exclamation-triangle" size={IconSize.SIZE_ICON_16} className="spacing-m-r-2 content-danger" />
        <Text className="content-danger" size={TextSize.XSMALL} val={titleErrorMessage} />
      </div>
    );
  };

  const renderInstagramFields = (): ReactElement => {
    return (
      <>
        <SocialPostCaption account={account} />
        <ReelMusicField account={account} isVideoDesign={isVideoDesign} />
      </>
    );
  };

  const renderFacebookVideoFields = (): ReactElement => {
    return (
      <>
        <ReelToggler
          disabledTooltipText={isAnyPosterResizingForForAnyMediaType ? window.i18next.t('pmwjs_your_design_is_being_resized') : undefined}
          isDisabled={isAnyPosterResizingForForAnyMediaType}
          isToggled={isReelMediaType}
          onReelToggle={onReelToggle}
        />
        <FieldSpacer />
        <TitleInput account={account} />
        <FieldSpacer />
        <SocialPostCaption account={account} />
      </>
    );
  };

  const renderFacebookGroupVideoFields = (): ReactElement => {
    return (
      <>
        <TitleInput account={account} />
        <FieldSpacer />
        <SocialPostCaption account={account} />
      </>
    );
  };

  const renderLinkedInPersonalVideoFields = (): ReactElement => {
    return (
      <>
        <TitleInput setTitleError={setTitleError} account={account} />
        <FieldSpacer />
        <SocialPostCaption account={account} />
        {getSpacerAfterCaption()}
        <LinkedInPrivacySelector account={account} />
        <FieldSpacer />
      </>
    );
  };

  const renderLinkedInPersonalImageFields = (): ReactElement => {
    return (
      <>
        <SocialPostCaption account={account} />
        {getSpacerAfterCaption()}
        <LinkedInPrivacySelector account={account} />
      </>
    );
  };

  const renderLinkedInPageVideoFields = (): ReactElement => {
    return (
      <>
        <TitleInput setTitleError={setTitleError} account={account} />
        <FieldSpacer />
        <SocialPostCaption account={account} />
      </>
    );
  };

  const renderTikTokImageFields = (): ReactElement => {
    return (
      <>
        <TitleInput account={account} />
        <FieldSpacer />
        <SocialPostCaption account={account} />
        {getSpacerAfterCaption()}
        <TiktokPublishingOptions account={account} />
      </>
    );
  };

  const renderTikTokVideoFields = (): ReactElement => {
    return (
      <>
        <SocialPostCaption account={account} />
        {getSpacerAfterCaption()}
        <TiktokPublishingOptions account={account} />
      </>
    );
  };

  const renderYouTubeImageFields = (): ReactElement => {
    return (
      <>
        <TitleInput setTitleError={setTitleError} account={account} areURLsAllowed={false} />
        <FieldSpacer />
        <SocialPostCaption account={account} />
        {getSpacerAfterCaption()}
        <YouTubePrivacySelector account={account} />
      </>
    );
  };

  const renderYouTubeVideoFields = (): ReactElement => {
    return (
      <>
        <TitleInput setTitleError={setTitleError} account={account} areURLsAllowed={false} />
        <FieldSpacer />
        <SocialPostCaption account={account} />
        {getSpacerAfterCaption()}
        <YouTubePrivacySelector account={account} />
        {renderTitleError()}
      </>
    );
  };

  const renderPinterestImageFields = (): ReactElement => {
    return (
      <>
        <PinterestBoardId account={account} />
        <FieldSpacer />
        <TitleInput setTitleError={setTitleError} account={account} />
        <FieldSpacer />
        <SocialPostCaption account={account} />
        {getSpacerAfterCaption()}
        <PinterestWebsiteLink account={account} />
        <FieldSpacer />
      </>
    );
  };

  const renderPinterestVideoFields = (): ReactElement => {
    return (
      <>
        <PinterestBoardId account={account} />
        <FieldSpacer />
        <TitleInput setTitleError={setTitleError} account={account} />
        <FieldSpacer />
        <SocialPostCaption account={account} />
        {getSpacerAfterCaption()}
        <PinterestWebsiteLink account={account} />
      </>
    );
  };

  switch (account.type) {
    case SocialPlatform.FACEBOOK:
    case SocialPlatform.FACEBOOK_PROFILE:
    case SocialPlatform.FACEBOOK_PAGE:
      if (isVideoDesign) {
        return renderFacebookVideoFields();
      }
      return <SocialPostCaption account={account} />;

    case SocialPlatform.FACEBOOK_GROUP:
      if (isVideoDesign) {
        return renderFacebookGroupVideoFields();
      }
      return <SocialPostCaption account={account} />;

    case SocialPlatform.INSTAGRAM:
      return renderInstagramFields();

    case SocialPlatform.TWITTER:
      return <SocialPostCaption account={account} />;

    case SocialPlatform.GOOGLE_BUSINESS_PROFILE:
      if (isVideoDesign) {
        return null;
      }
      return <SocialPostCaption account={account} />;

    case SocialPlatform.THREADS:
      return <SocialPostCaption account={account} />;

    case SocialPlatform.LINKEDIN:
    case SocialPlatform.LINKEDIN_PROFILE:
      if (isVideoDesign) {
        return renderLinkedInPersonalVideoFields();
      }
      return renderLinkedInPersonalImageFields();

    case SocialPlatform.LINKEDIN_PAGE:
      if (isVideoDesign) {
        return renderLinkedInPageVideoFields();
      }
      return <SocialPostCaption account={account} />;

    case SocialPlatform.TIKTOK:
      if (isVideoDesign) {
        return renderTikTokVideoFields();
      }
      return renderTikTokImageFields();

    case SocialPlatform.YOUTUBE:
      if (isVideoDesign) {
        return renderYouTubeVideoFields();
      }
      return renderYouTubeImageFields();

    case SocialPlatform.PINTEREST:
      if (isVideoDesign) {
        return renderPinterestVideoFields();
      }
      return renderPinterestImageFields();

    default:
      return null;
  }
}
